import './ScheduleDatesHome.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { datesFirstCaps, months } from '../../constants/dates';
import {
  apiDateToDate,
  getEasternTime,
  stringToMonthDate
} from '../../helpers/dateHelpers';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';

export default function ScheduleDatesHome({ currentPairingMonthData }) {
  const freezePeriodContext = useFreezePeriod();
  const { t } = useTranslation();
  const currentPairingMonth = currentPairingMonthData.pairingMonth;
  const biddingClosed = apiDateToDate(
    currentPairingMonthData?.biddingClosed?.start
  );
  // const pairingFreezed = apiDateToDate(currentPairingMonthData?.pairingFreezed?.start);
  const pairingPosted = apiDateToDate(
    currentPairingMonthData?.pairingPosted?.start
  );
  const yearForCurrentPairingMonth = currentPairingMonthData.pairingYear;

  const [flyingSchedule, setFlyingSchedule] = useState<any>([]);
  const [responseDelayFlag, setResponseDelayFlag] = useState<any>([]);
  const [sdoSubmission, setSdoSubmission] = useState<any>([]);
  const [simSubmission, setSimSubmission] = useState<any>([]);
  const [artSubmission, setArtSubmission] = useState<any>([]);

  useEffect(() => {
    if (
      !freezePeriodContext?.freezePeriodData &&
      !freezePeriodContext?.freezePeriodData?.sdoSubmission &&
      !freezePeriodContext?.freezePeriodData?.flyingSchedule &&
      !freezePeriodContext?.freezePeriodData?.simSubmission &&
      !freezePeriodContext?.freezePeriodData?.artSubmission
    )
      return;

    setSdoSubmission(freezePeriodContext?.freezePeriodData?.sdoSubmission);
    setFlyingSchedule(freezePeriodContext?.freezePeriodData?.flyingSchedule);
    setSimSubmission(freezePeriodContext?.freezePeriodData?.simSubmission);
    setArtSubmission(freezePeriodContext?.freezePeriodData?.artSubmission);
    setResponseDelayFlag(
      freezePeriodContext.freezePeriodData?.sdoSubmission?.map(
        (item) => item.responseDelayFlag
      )
    );
  }, [freezePeriodContext]);

  const getHourNminute = (date) => {
    if (!date) return '';
    return (
      '@ ' +
      getTwoDigits(new Date(date).getHours()) +
      ':' +
      getTwoDigits(new Date(date).getMinutes()) +
      ' ET'
    );
  };

  const getMonthNday = (date) => {
    if (!date) return '';
    return (
      datesFirstCaps[new Date(date).getMonth()] +
      ' ' +
      getTwoDigits(new Date(date).getDate()) +
      ' '
    );
  };

  const getTwoDigits = (param) => (param < 10 ? '0' + param : param);

  const formatScheduledDate = (fromDate, toDate, simMonth) => {
    // Convert ISO date strings to format expected by getUtcDate function
    const convertIsoToExpectedFormat = (isoString) => {
      return isoString
        .replace('T', ' ')
        .replace('.000Z', '')
        .replace(/-/g, '/');
    };

    const fromFormatted = getMonthNday(
      getEasternTime(convertIsoToExpectedFormat(fromDate))
    );
    const toFormatted = getMonthNday(
      getEasternTime(convertIsoToExpectedFormat(toDate))
    );
    const timeFormatted = getHourNminute(
      getEasternTime(convertIsoToExpectedFormat(toDate))
    );

    const monthFormatted = dayjs()
      .month(simMonth - 1)
      .format('MMM');

    return `SIM for ${monthFormatted}: ${fromFormatted}- ${toFormatted}${timeFormatted}`;
  };

  const formatArtScheduledDate = (fromDate, toDate, artMonth) => {
    // Convert ISO date strings to format expected by getUtcDate function
    const convertIsoToExpectedFormat = (isoString) => {
      return isoString
        .replace('T', ' ')
        .replace('.000Z', '')
        .replace(/-/g, '/');
    };

    const fromFormatted = getMonthNday(
      getEasternTime(convertIsoToExpectedFormat(fromDate))
    );
    const toFormatted = getMonthNday(
      getEasternTime(convertIsoToExpectedFormat(toDate))
    );
    const timeFormatted = getHourNminute(
      getEasternTime(convertIsoToExpectedFormat(toDate))
    );

    const monthFormatted = dayjs()
      .month(artMonth - 1)
      .format('MMM');

    return `ART for ${monthFormatted}: ${fromFormatted}- ${toFormatted}${timeFormatted}`;
  };

  const handleOnMBMNotificationClick = (notification) => {
    sessionStorage.setItem(
      'block-month-notification',
      JSON.stringify({ month: notification.month, year: notification.year })
    );
    return false;
  };

  const responseDelayFlagValue = (e) => e === 'Y';

  return (
    <div className="scheduled">
      <span className="scheduled__title">
        {t('home.pairing-bidding-dates')}{' '}
      </span>
      <div>
        <span className="scheduled__next-pairing-month">
          {months[currentPairingMonth - 1]} {yearForCurrentPairingMonth}
        </span>
        {currentPairingMonthData?.pairingPosted?.isAdjusted ? (
          <p className="scheduled__adjusted-time">
            {t('home.adjustd-pairing-posted')}:{' '}
            <span>{stringToMonthDate(pairingPosted)}</span>
          </p>
        ) : (
          <p>
            {t('home.pairing-posted')}:{' '}
            <span>{stringToMonthDate(pairingPosted)}</span>
          </p>
        )}
        {currentPairingMonthData?.biddingClosed?.isAdjusted ? (
          <>
            <p className="scheduled__adjusted-time">
              {t('home.adjustd-bidding-closes')}:{' '}
              <span>{stringToMonthDate(biddingClosed)}</span>
            </p>
          </>
        ) : (
          <>
            <p>
              {t('home.bidding-closes')}:{' '}
              <span>{stringToMonthDate(biddingClosed)}</span>
            </p>
          </>
        )}
      </div>

      {flyingSchedule && flyingSchedule.length ? (
        <span className="scheduled__title">{t('home.flying-schedule')} </span>
      ) : (
        ''
      )}
      <div>
        <ul className="scheduled__fly-list">
          {flyingSchedule && flyingSchedule.length
            ? flyingSchedule.map((item) => (
                <li>
                  {item.month && item.year ? (
                    <a
                      href="/my-block-month"
                      className="scheduled__link"
                      onClick={() => handleOnMBMNotificationClick(item)}
                    >
                      {item.text}{' '}
                    </a>
                  ) : (
                    item.text
                  )}
                </li>
              ))
            : ''}
        </ul>
      </div>

      {sdoSubmission && sdoSubmission.length ? (
        <span className="scheduled__title">
          {t('home.sdo-submission-schedule')}{' '}
        </span>
      ) : (
        ''
      )}
      <div className="shipment-details-data">
        <ul className="scheduled__fly-list">
          {sdoSubmission && sdoSubmission.length
            ? sdoSubmission.map((items, index) => (
                <li>
                  {(() => {
                    if (index === 1) {
                      return (
                        <>
                          <a href="/sdo-landing" className="scheduled__link">
                            <span className="scheduled__info">
                              <div className="scheduled__info-content">
                                <p>
                                  {`${t('home.sod-for')} ${datesFirstCaps[items.data?.sdo_month - 1]}: `}{' '}
                                  {responseDelayFlag.some(
                                    responseDelayFlagValue
                                  )
                                    ? `${t('home.pendingAward')}`
                                    : `${items.data?.from_date ? getMonthNday(getEasternTime(items.data?.from_date.replace(/-/g, '/'))) : 'N/A'} - ${items.data?.to_date ? getMonthNday(getEasternTime(items.data?.to_date.replace(/-/g, '/'))) : 'N/A'}`}
                                </p>
                                <p className="scheduled__info-time">
                                  {responseDelayFlag.some(
                                    responseDelayFlagValue
                                  )
                                    ? ' '
                                    : `${items.data?.to_date ? getHourNminute(getEasternTime(items.data?.to_date.replace(/-/g, '/'))) : ''}`}
                                </p>
                              </div>
                            </span>
                          </a>
                        </>
                      );
                    } else if (index === 2) {
                      return (
                        <>
                          <span className="scheduled__info">
                            <div className="scheduled__info-content">
                              <p>
                                {`${t('home.sod-for')} ${datesFirstCaps[items.data?.sdo_month - 1]}: `}{' '}
                                {`${items.data?.from_date ? getMonthNday(getEasternTime(items.data?.from_date.replace(/-/g, '/'))) : 'N/A'} - ${items.data?.to_date ? getMonthNday(getEasternTime(items.data?.to_date.replace(/-/g, '/'))) : 'N/A'}`}
                              </p>
                              <p className="scheduled__info-time">{`${items.data?.to_date ? getHourNminute(getEasternTime(items.data?.to_date.replace(/-/g, '/'))) : ''}${' '}`}</p>
                            </div>
                          </span>
                        </>
                      );
                    } else {
                      return <></>;
                    }
                  })()}
                </li>
              ))
            : ''}
        </ul>
      </div>
      {(simSubmission && simSubmission.length) ||
      (artSubmission && artSubmission.length) ? (
        <Grid>
          <span className="scheduled__title">SIM / ART Request Schedule</span>
          <ul className="scheduled__fly-list">
            {simSubmission &&
              simSubmission.map((item, index) => (
                <li key={`sim-${index}`}>
                  {item.href ? (
                    <a href={`/sim-request`} className="scheduled__link">
                      <span className="scheduled__info">
                        <div className="scheduled__info-content">
                          {formatScheduledDate(
                            item.from_date,
                            item.to_date,
                            parseInt(item.sim_month)
                          )}
                        </div>
                      </span>
                    </a>
                  ) : (
                    <span className="scheduled__info">
                      <div className="scheduled__info-content">
                        {formatScheduledDate(
                          item.from_date,
                          item.to_date,
                          parseInt(item.sim_month)
                        )}
                      </div>
                    </span>
                  )}
                </li>
              ))}
            {artSubmission &&
              artSubmission.map((item, index) => (
                <li key={`art-${index}`}>
                  {item.href ? (
                    <a href={`/art-request`} className="scheduled__link">
                      <span className="scheduled__info">
                        <div className="scheduled__info-content">
                          {formatArtScheduledDate(
                            item.from_date,
                            item.to_date,
                            parseInt(item.art_month)
                          )}
                        </div>
                      </span>
                    </a>
                  ) : (
                    <span className="scheduled__info">
                      <div className="scheduled__info-content">
                        {formatArtScheduledDate(
                          item.from_date,
                          item.to_date,
                          parseInt(item.art_month)
                        )}
                      </div>
                    </span>
                  )}
                </li>
              ))}
          </ul>
          <br />
          <br />
        </Grid>
      ) : (
        ''
      )}
    </div>
  );
}
